import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Input, Field } from "@/components/admin/ui";
import { ECV_GROUPS, ECV_TAGS } from "@/utils/ecvAttrs";

const FieldInput = ({ def, a, set }) => {
  const tid = `ecv-${def.field}`;
  if (def.type === "yn") {
    return (
      <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F6F1E4] py-1" data-testid={tid}>
        <input type="checkbox" checked={!!a[def.field]} onChange={(e) => set(def.field, e.target.checked)} className="accent-[#FFD166]" />
        {def.label}
      </label>
    );
  }
  if (def.type === "sel") {
    return (
      <Field label={def.label}>
        <select value={a[def.field] ?? ""} onChange={(e) => set(def.field, e.target.value === "" ? null : e.target.value)} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid={tid}>
          <option value="">—</option>
          {def.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
    );
  }
  if (def.type === "num") {
    return (
      <Field label={def.unit ? `${def.label} (${def.unit})` : def.label}>
        <Input type="number" min="0" step={def.step} value={a[def.field] ?? ""} onChange={(e) => set(def.field, e.target.value === "" ? null : Number(e.target.value))} data-testid={tid} />
      </Field>
    );
  }
  return <Field label={def.label}><Input value={a[def.field] ?? ""} onChange={(e) => set(def.field, e.target.value)} placeholder={def.placeholder} data-testid={tid} /></Field>;
};

/* Hidden admin-only ECV specs — never rendered in the public description. */
export const EcvAttributesEditor = ({ value, onChange }) => {
  const [open, setOpen] = useState(!!value?.include_in_quiz || !!value?.include_in_comparison);
  const a = value || {};
  const set = (field, v) => onChange({ ...a, [field]: v });
  const tags = a.recommendation_tags || [];
  const toggleTag = (t) => set("recommendation_tags", tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);
  return (
    <div className="mt-6 border-t border-[#FFD166]/15 pt-5" data-testid="ecv-attrs-section">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left" data-testid="ecv-attrs-toggle">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166]">ECV attributes (admin only)</div>
          <p className="text-xs text-[#B9C8D5] mt-0.5">Specs that will power ECV comparison, recommendations, filters and the ECV quiz. Not shown in the public description.</p>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#FFD166]" /> : <ChevronRight className="w-4 h-4 text-[#FFD166]" />}
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          {ECV_GROUPS.map((g) => (
            <div key={g.title} className="rounded-xl border border-[#FFD166]/15 bg-black/20 p-3" data-testid={`ecv-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166] mb-2">{g.title}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.fields.filter((f) => f.type !== "yn").map((f) => <FieldInput key={f.field} def={f} a={a} set={set} />)}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 mt-2">
                {g.fields.filter((f) => f.type === "yn").map((f) => <FieldInput key={f.field} def={f} a={a} set={set} />)}
              </div>
            </div>
          ))}
          <div className="rounded-xl border border-[#FFD166]/15 bg-black/20 p-3" data-testid="ecv-group-tags">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166] mb-2">Recommendation tags</div>
            <div className="flex flex-wrap gap-2">
              {ECV_TAGS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => toggleTag(v)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tags.includes(v) ? "bg-[#FFD166]/15 border-[#FFD166] text-[#FFD166]" : "border-[#FFD166]/25 text-[#B9C8D5] hover:border-[#FFD166]/50"}`} data-testid={`ecv-tag-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <Field label="Internal notes"><textarea value={a.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm" data-testid="ecv-notes" /></Field>
        </div>
      )}
    </div>
  );
};

export default EcvAttributesEditor;
